import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';
import {AnimalModel} from '../models/animal.model';
import {AnimalMediaModel} from '../models/animal-media.model';
import {AnimalImagesRepository} from '../states/animal-images.repository';
import {AnimalRepository} from '../states/animal.repository';
import {Subject, filter, forkJoin, merge, take, takeUntil} from 'rxjs';
import {TranslocoService} from '@jsverse/transloco';
import {AnimalMediaCategoryModel} from '../models/animal-media-category.model';
import {AnimalImageService} from '../services/animal-image.service';
import {AnimalVideoRepository} from '../states/animal-video.repository';
import {AnimalMediaCategory} from '../models/animal-media-category.enum';
import {ConfirmationService} from 'primeng/api';
import {environment} from 'src/environment/environment';
import {TokenService} from "../../../shared/services/token.service";
import { HelperService } from 'src/app/shared/services/helper.service';

@Component({
  selector: 'app-animal-details-gallery',
  templateUrl: './animal-details-gallery.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AnimalDetailsGalleryComponent implements OnInit, OnDestroy {
  @Input() animal: AnimalModel | null = null;

  media: (AnimalMediaModel & { translatedCategories?: string; uiDate?: Date | null })[] = [];
  mediaCategoryOptions: AnimalMediaCategoryModel[] = [];
  flippedItems: Record<number, boolean> = {};
  isMultiSelectMode = false;
  selectedMediaIds = new Set<number>();

  private readonly destroy$ = new Subject<void>();

  filter = {
    showImages: true,
    showVideos: true,
    syncWithWebsite: false,
    sharePointImport: false,
    selectedCategories: [] as AnimalMediaCategory[]
  };

  constructor(
    private readonly _animalImagesRepository: AnimalImagesRepository,
    private readonly _animalVideosRepository: AnimalVideoRepository,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _helperService: HelperService,
    public readonly _translateService: TranslocoService,
    private readonly _animalImageService: AnimalImageService,
    private readonly _confirmationService: ConfirmationService,
    private readonly _tokenService: TokenService
  ) {
  }

  ngOnInit(): void {
    if (!this.animal) return;

    forkJoin({
      images: this._animalImagesRepository.getImages(this.animal.id),
      videos: this._animalVideosRepository.getVideos(this.animal.id)
    }).subscribe(({images, videos}) => {
      this.media = [...images, ...videos].map((item: AnimalMediaModel) => ({
        ...item,
        uiDate: item.date ? new Date(item.date) : null,
        translatedCategories: this.getTranslatedCategories(item.categories)
      }));
      this._cdr.markForCheck();
    });

    merge(
      this._animalImagesRepository.imageAdded$,
      this._animalVideosRepository.videoAdded$
    )
      .pipe(
        takeUntil(this.destroy$),
        filter((newItem): newItem is AnimalMediaModel => !!newItem),
        filter((newItem) => this.animal != null && newItem.animalId === this.animal.id)
      )
      .subscribe((newItem) => {
        const alreadyExists = this.media.some(m => m.id === newItem.id);
        if (!alreadyExists) {
          this.media = [
            ...this.media,
            {
              ...newItem,
              uiDate: newItem.date ? new Date(newItem.date) : null,
              translatedCategories: this.getTranslatedCategories(newItem.categories)
            }
          ];
          this._cdr.markForCheck();
        }
      });

    this.mediaCategoryOptions = this._animalImageService.getAnimalImageCategories();
    this._translateService
      .selectTranslateObject('animal.imageCategory')
      .pipe(take(1))
      .subscribe((translations) => {
        this.mediaCategoryOptions = Object.values(AnimalMediaCategory).map((cat) => ({
          value: cat,
          name: translations[cat] ?? cat
        }));
        this._cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFilterChange(): void {
    this._cdr.markForCheck();
  }

  private getTranslatedCategories(categories: string[] | null | undefined): string {
    if (!categories || categories.length === 0) return '';
    return categories
      .map((cat) =>
        this._translateService.translate(`animal.imageCategory.${cat}`)
      )
      .join(', ');
  }

  toggleFlip(id: number, event: MouseEvent): void {
    event.stopPropagation();
    if (this.isMultiSelectMode) return;

    this.flippedItems[id] = !this.flippedItems[id];
    Object.keys(this.flippedItems).forEach(key => {
      if (key !== id.toString()) {
        this.flippedItems[+key] = false;
      }
    });
    this._cdr.markForCheck();
  }

  onCategoryChange(image: AnimalMediaModel | any): void {
    if (!this.animal) return;
    const updatedCategories: AnimalMediaCategory[] = image.categories ?? [];
    this._animalImagesRepository.updateImage(image.id, {categories: updatedCategories}, this.animal.id);
    image.translatedCategories = this.getTranslatedCategories(updatedCategories);
    this._cdr.markForCheck();
  }

  get filteredMedia(): (AnimalMediaModel & { translatedCategories?: string })[] {
    return this.media.filter((item) => {
      const isImage = item.type === 'image';
      const isVideo = item.type === 'video';

      const passesType =
        (this.filter.showImages && isImage) || (this.filter.showVideos && isVideo);

      const passesSync =
        (!this.filter.syncWithWebsite || item.syncWithWebsite) &&
        (!this.filter.sharePointImport || item.syncWithSharedFolder);

      const passesCategory =
        this.filter.selectedCategories.length === 0 ||
        item.categories?.some((cat) => this.filter.selectedCategories.includes((cat as AnimalMediaCategory)));

      return passesType && passesSync && passesCategory;
    });
  }

  toggleSharedFolder(media: AnimalMediaModel) {
    if (!this.animal || this.isMultiSelectMode) return;
    const next = !media.syncWithSharedFolder;

    if (media.type === 'image') {
      this._animalImagesRepository.updateImage(media.id, { syncWithSharedFolder: next }, this.animal.id);
    } else {
      this._animalVideosRepository.updateVideo(media.id, { syncWithSharedFolder: next }, this.animal.id);
    }

    media.syncWithSharedFolder = next;
    this._cdr.markForCheck();
  }

  toggleWebsite(media: AnimalMediaModel) {
    if (!this.animal || this.isMultiSelectMode) return;
    const next = !media.syncWithWebsite;

    if (media.type === 'image') {
      this._animalImagesRepository.updateImage(media.id, { syncWithWebsite: next }, this.animal.id);
    } else {
      this._animalVideosRepository.updateVideo(media.id, { syncWithWebsite: next }, this.animal.id);
    }

    media.syncWithWebsite = next;
    this._cdr.markForCheck();
  }

  togglePreview(media: AnimalMediaModel) {
    if (!this.animal || this.isMultiSelectMode) return;
    const next = !media.preview;
    if(media.type !== 'image') return;

    this.media = this.media.map((item) =>
      item.id === media.id ? {...item, preview: !item.preview} : {...item, preview: false}
    );

    this._animalImagesRepository.updateImage(media.id, {preview: next}, this.animal.id);
    media.preview = next;
    this._cdr.markForCheck();
  }

  onDateChange(item: AnimalMediaModel & { uiDate?: Date | null }) {
    if (!this.animal) return;

    let isoDate: string | null = null;

    if (item.uiDate) {
      const d = item.uiDate;
      const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      isoDate = utc.toISOString();
    }

    if (item.type === 'image') {
      this._animalImagesRepository.updateImage(item.id, {date: isoDate}, this.animal.id);
    } else if (item.type === 'video') {
      this._animalVideosRepository.updateVideo(item.id, {date: isoDate}, this.animal.id);
    }

    this._cdr.markForCheck();
  }

  deleteMedia(media: AnimalMediaModel) {
    this._confirmationService.confirm({
      message: this._translateService.translate('delete_confirmation'),
      acceptLabel: this._translateService.translate('yes'),
      rejectLabel: this._translateService.translate('no'),
      header: '',
      icon: 'pi pi-info-circle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-text',
      acceptIcon: 'none',
      rejectIcon: 'none',
      accept: () => {
        if (!this.animal) return;
        this.deleteMediaByType(media).subscribe(() => {
          this.media = this.media.filter((m) => m.id !== media.id);
          this.selectedMediaIds.delete(media.id);
          this._cdr.markForCheck();
        });
      }
    });
  }

  multiSelectMode() {
    this.isMultiSelectMode = !this.isMultiSelectMode;
    if (this.isMultiSelectMode) {
      Object.keys(this.flippedItems).forEach(key => {
        this.flippedItems[+key] = false;
      });
    } else {
      this.selectedMediaIds.clear();
    }
    this._cdr.markForCheck();
  }

  toggleSelect(id: number) {
    if (!this.isMultiSelectMode) return;
    this.selectedMediaIds.has(id) ? this.selectedMediaIds.delete(id) : this.selectedMediaIds.add(id);
    this._cdr.markForCheck();
  }

  toggleSharepointForSelected() {
    if (!this.animal) return;
    this.media
      .filter((m) => this.selectedMediaIds.has(m.id))
      .forEach((m) => {
        const next = !m.syncWithSharedFolder;
        if (m.type === 'image') {
          this._animalImagesRepository.updateImage(m.id, {syncWithSharedFolder: next}, this.animal!.id);
        } else if (m.type === 'video') {
          this._animalVideosRepository.updateVideo(m.id, {syncWithSharedFolder: next}, this.animal!.id);
        }
        m.syncWithSharedFolder = next;
        this.resetMultiSelectMode();
      });
    this._cdr.markForCheck();
  }

  toggleWebsiteForSelected() {
    if (!this.animal) return;
    this.media
      .filter((m) => this.selectedMediaIds.has(m.id))
      .forEach((m) => {
        const next = !m.syncWithWebsite;
        if (m.type === 'image') {
          this._animalImagesRepository.updateImage(m.id, {syncWithWebsite: next}, this.animal!.id);
        } else if (m.type === 'video') {
          this._animalVideosRepository.updateVideo(m.id, {syncWithWebsite: next}, this.animal!.id);
        }
        m.syncWithWebsite = next;
        this.resetMultiSelectMode();
      });
    this._cdr.markForCheck();
  }

  deleteSelectedMedia() {
    if (!this.animal || this.selectedMediaIds.size === 0) return;

    this._confirmationService.confirm({
      message: this._translateService.translate('delete_confirmation'),
      acceptLabel: this._translateService.translate('yes'),
      rejectLabel: this._translateService.translate('no'),
      header: '',
      icon: 'pi pi-info-circle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-text',
      acceptIcon: 'none',
      rejectIcon: 'none',
      accept: () => {
        const ids = Array.from(this.selectedMediaIds);
        ids.forEach((id) => {
          const media = this.media.find((m) => m.id === id);
          if (!media) return;
          this.deleteMediaByType(media).subscribe(() => {
            this.media = this.media.filter((m) => m.id !== media.id);
            this.selectedMediaIds.delete(media.id);
            this.resetMultiSelectMode();
            this._cdr.markForCheck();
          });
        });
      }
    });
  }
  private deleteMediaByType(media: AnimalMediaModel) {
    if (!this.animal) return {
      subscribe: () => {
      }
    } as any;
    if (media.type === 'image') {
      return this._animalImagesRepository.deleteImage(media.id, this.animal.id).pipe(take(1));
    } else if (media.type === 'video') {
      return this._animalVideosRepository.deleteVideo(media.id, this.animal.id).pipe(take(1));
    }
    // Fallback: leeres Observable
    return {
      subscribe: () => {
      }
    } as any;
  }

  resetMultiSelectMode() {
    this.isMultiSelectMode = false;
    this.selectedMediaIds.clear();
    this._cdr.markForCheck();
  }

  private async downloadMedia(media: AnimalMediaModel) {
    if (!media.contentUrl) return;
    const token = this._tokenService.getAccessToken();
    if (!token) {
      return;
    }

    const apiUrl = environment.apiUrl;
    let url = `${apiUrl}${media.contentUrl}?x-auth-token=${encodeURIComponent(token)}`;
    url = url.replace('/api/', '/');
    const filename = `media_${media.id}${this.getFileExtensionFromUrl(media.contentUrl)}`;

    try {
      const response = await fetch(url, {
        headers: {'x-auth-token': token}
      });
      if (!response.ok) throw new Error('Download fehlgeschlagen');
      const blob = await response.blob();

      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e) {
      console.error('Download fehlgeschlagen', e);
    }
  }

  private getFileExtensionFromUrl(url: string): string {
    const match = url.match(/\.[a-zA-Z0-9]+(?=\?|$)/);
    return match ? match[0] : '';
  }

  downloadSingleMedia(media: AnimalMediaModel) {
    this.downloadMedia(media);
  }

  downloadSelectedMedia() {
    if (this.selectedMediaIds.size === 0) return;
    const selected = this.media.filter(m => this.selectedMediaIds.has(m.id));
    selected.forEach((media, idx) => {
      setTimeout(() => this.downloadMedia(media), idx * 300);
    });
    this.resetMultiSelectMode();
  }
}
